import React, { useRef, useState } from 'react';

import type { AlignmentType } from '@aline/common';
import { Paper, Popover } from '@mui/material';
import { AlineTooltip, SelectButton } from 'src/components';
import { CenterAlignIcon } from 'src/components/icons/CenterAlignIcon';
import { JustifyIcon } from 'src/components/icons/JustifyIcon';
import { LeftAlignIcon } from 'src/components/icons/LeftAlignIcon';
import { RightAlignIcon } from 'src/components/icons/RightAlignIcon';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import type { ParagraphProperties } from 'src/editor/editor-derived-state';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

interface AlignmentIconProps {
  alignment: ParagraphProperties['alignment'];
}

function AlignmentIcon(props: AlignmentIconProps): JSX.Element {
  const { alignment } = props;

  switch (alignment) {
    case 'left':
      return <LeftAlignIcon />;
    case 'center':
      return <CenterAlignIcon />;
    case 'right':
      return <RightAlignIcon />;
    case 'justify':
      return <JustifyIcon />;
    default:
      return <LeftAlignIcon />;
  }
}

const useStyles = makeStyles()(theme => ({
  arrowIcon: {
    height: 12,
    marginLeft: theme.spacing(0.25),
    width: 12,
  },
  paper: {
    padding: theme.spacing(1),
    zIndex: theme.zIndex.tooltip,
  },
  selectButton: {
    height: 32,
    width: 46,
  },
  toggleButton: {
    height: 32,
    width: 32,
  },
  toggleButtonContainer: {
    display: 'flex',
  },
}));

export const alignmentButtonAttribute = 'alignment-button';
export const leftAlignButtonAttribute = 'left-align-button';
export const centerAlignButtonAttribute = 'center-align-button';
export const rightAlignButtonAttribute = 'right-align-button';
export const justifyButtonAttribute = 'justify-button';

export function AlignmentButtons(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const currentUserId = useCurrentUserId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const alignment = useEditorTabStateSelector(state => state.derived.paragraphProperties.alignment);

  function handleClick(alignment: AlignmentType) {
    return (): void => {
      if (!currentUserId || !editingMode) return;

      AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, { alignment });

      setPopoverOpen(false);

      ClientTransforms.focus(editor);
    };
  }

  function handleTooltipOpen(): void {
    setTooltipOpen(true);
  }

  function handleTooltipClose(): void {
    setTooltipOpen(false);
  }

  return (
    <>
      <AlineTooltip
        arrow
        open={tooltipOpen && !popoverOpen}
        placement="bottom"
        title="Align"
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
      >
        <div>
          <SelectButton
            ref={buttonRef}
            className={classes.selectButton}
            data-cy={alignmentButtonAttribute}
            disabled={editorLoading}
            selected={popoverOpen}
            onClick={(): void => {
              setPopoverOpen(!popoverOpen);
            }}
          >
            <AlignmentIcon alignment={alignment} />
            <SmallDownArrowIcon className={classes.arrowIcon} />
          </SelectButton>
        </div>
      </AlineTooltip>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={popoverOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={(): void => {
          setPopoverOpen(false);
        }}
      >
        <Paper className={classes.paper}>
          <div className={classes.toggleButtonContainer}>
            <AlineTooltip arrow placement="bottom" title="Align left">
              <SelectButton
                className={classes.toggleButton}
                data-cy={leftAlignButtonAttribute}
                selected={alignment === 'left'}
                onClick={handleClick('left')}
              >
                <LeftAlignIcon />
              </SelectButton>
            </AlineTooltip>
            <AlineTooltip arrow placement="bottom" title="Align center">
              <SelectButton
                className={classes.toggleButton}
                data-cy={centerAlignButtonAttribute}
                selected={alignment === 'center'}
                onClick={handleClick('center')}
              >
                <CenterAlignIcon />
              </SelectButton>
            </AlineTooltip>
            <AlineTooltip arrow placement="bottom" title="Align right">
              <SelectButton
                className={classes.toggleButton}
                data-cy={rightAlignButtonAttribute}
                selected={alignment === 'right'}
                onClick={handleClick('right')}
              >
                <RightAlignIcon />
              </SelectButton>
            </AlineTooltip>
            <AlineTooltip arrow placement="bottom" title="Justify">
              <SelectButton
                className={classes.toggleButton}
                data-cy={justifyButtonAttribute}
                selected={alignment === 'justify'}
                onClick={handleClick('justify')}
              >
                <JustifyIcon />
              </SelectButton>
            </AlineTooltip>
          </div>
        </Paper>
      </Popover>
    </>
  );
}
